import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, MapPin, Sun, Cloud, CloudRain, Snowflake, Clock, 
  Image, Upload, X, GripVertical, ChevronUp, ChevronDown, Euro, Users,
  Compass, Calendar, Phone, ExternalLink, AlertTriangle, Thermometer, Wind
} from 'lucide-react';

import { API, BASE_URL } from '../../lib/api';

const WEATHER_CONDITIONS = [
  { value: 'sunny', label: 'Soleggiato', icon: Sun },
  { value: 'cloudy', label: 'Nuvoloso', icon: Cloud },
  { value: 'rainy', label: 'Pioggia', icon: CloudRain },
  { value: 'cold', label: 'Freddo', icon: Snowflake },
];

const CATEGORIES = [
  { value: 'spiaggia', label: 'Spiaggia' },
  { value: 'cultura', label: 'Cultura' },
  { value: 'natura', label: 'Natura' },
  { value: 'gastronomia', label: 'Gastronomia' },
  { value: 'relax', label: 'Relax' },
  { value: 'shopping', label: 'Shopping' },
];

const PUBBLICO_OPTIONS = [
  { value: 'famiglie', label: 'Famiglie' },
  { value: 'coppie', label: 'Coppie' },
  { value: 'bambini', label: 'Bambini' },
  { value: 'anziani', label: 'Anziani' },
  { value: 'sportivi', label: 'Sportivi' },
  { value: 'tutti', label: 'Tutti' },
];

const DIFFICOLTA_OPTIONS = [
  { value: 'facile', label: 'Facile' },
  { value: 'medio', label: 'Medio' },
  { value: 'difficile', label: 'Difficile' },
];

const emptyFormData = {
  nome: '',
  descrizione: '',
  condizione_meteo: 'sunny',
  categoria: 'cultura',
  luogo: '',
  durata: '',
  immagine_url: '',
  galleria: [],
  link_esterno: '',
  ordine: 0,
  attivo: true,
  distanza_km: '',
  tempo_percorrenza: '',
  costo_indicativo: '',
  cosa_portare: '',
  indicazioni: '',
  pubblico: [],
  difficolta: '',
  orari_apertura: '',
  tappe: [],
  prenotabile: false,
  prenotazione_link: '',
  prenotazione_whatsapp: '',
  prenotazione_note: '',
  min_partecipanti: '',
  max_partecipanti: '',
  meteo_config: {
    meteo_ideale: [],
    meteo_escluso: [],
    temperatura_min: '',
    temperatura_max: '',
    vento_max_kmh: '',
    attivita_indoor: false,
    attivita_outdoor: true,
    attivita_marina: false,
  }
};

const emptyStage = {
  ordine: 0,
  orario: '',
  titolo: '',
  descrizione: '',
  localita: '',
  durata: '',
  link_maps: '',
  attivita_consigliata: ''
};

// Image Upload Component
const ImageUploader = ({ value, onChange, label, token }) => {
  const [uploading, setUploading] = useState(false);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('nome', file.name);
    
    try {
      const response = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Get the URL from response
      const imageUrl = response.data.url || response.data.file_url;
      if (imageUrl) {
        // Ensure we use the API path
        const apiUrl = imageUrl.replace('/uploads/', '/api/uploads/');
        onChange(`${BASE_URL}${apiUrl}`);
        toast.success('Immagine caricata!');
      }
    } catch (error) {
      toast.error('Errore upload immagine');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {value ? (
        <div className="relative">
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-40 object-cover rounded-lg border"
            onError={(e) => e.target.style.display = 'none'}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            id={`upload-${label}`}
            disabled={uploading}
          />
          <label 
            htmlFor={`upload-${label}`}
            className="cursor-pointer flex flex-col items-center gap-2 text-gray-500 hover:text-[#C5A059]"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]" />
            ) : (
              <>
                <Upload className="w-8 h-8" />
                <span className="text-sm">Carica immagine</span>
              </>
            )}
          </label>
        </div>
      )}
      <Input
        placeholder="Oppure inserisci URL immagine"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs"
      />
    </div>
  );
};

// Gallery Uploader Component
const GalleryUploader = ({ images, onChange, token }) => {
  const [uploading, setUploading] = useState(false);
  
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newImages = [...images];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('nome', file.name);
      
      try {
        const response = await axios.post(`${API}/admin/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const imageUrl = response.data.url || response.data.file_url;
        if (imageUrl) {
          const apiUrl = imageUrl.replace('/uploads/', '/api/uploads/');
          newImages.push(`${BASE_URL}${apiUrl}`);
        }
      } catch (error) {
        toast.error(`Errore upload ${file.name}`);
      }
    }
    
    onChange(newImages);
    toast.success('Immagini caricate!');
    setUploading(false);
    e.target.value = '';
  };
  
  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };
  
  const moveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    onChange(newImages);
  };
  
  return (
    <div className="space-y-2">
      <Label>Galleria fotografica</Label>
      <div className="grid grid-cols-3 gap-2">
        {images.map((img, index) => (
          <div key={index} className="relative group">
            <img 
              src={img} 
              alt={`Gallery ${index + 1}`}
              className="w-full h-24 object-cover rounded-lg border"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
              <button type="button" onClick={() => moveImage(index, -1)} className="p-1 bg-white rounded">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => moveImage(index, 1)} className="p-1 bg-white rounded">
                <ChevronDown className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => removeImage(index)} className="p-1 bg-red-500 text-white rounded">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg h-24 flex items-center justify-center">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id="gallery-upload"
            disabled={uploading}
          />
          <label 
            htmlFor="gallery-upload"
            className="cursor-pointer flex flex-col items-center gap-1 text-gray-500 hover:text-[#C5A059]"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C5A059]" />
            ) : (
              <>
                <Plus className="w-6 h-6" />
                <span className="text-xs">Aggiungi</span>
              </>
            )}
          </label>
        </div>
      </div>
    </div>
  );
};

// Stages Editor Component
const StagesEditor = ({ stages, onChange }) => {
  const addStage = () => {
    const newStage = { ...emptyStage, ordine: stages.length };
    onChange([...stages, newStage]);
  };
  
  const updateStage = (index, field, value) => {
    const newStages = [...stages];
    newStages[index] = { ...newStages[index], [field]: value };
    onChange(newStages);
  };
  
  const removeStage = (index) => {
    const newStages = stages.filter((_, i) => i !== index);
    newStages.forEach((s, i) => s.ordine = i);
    onChange(newStages);
  };
  
  const moveStage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= stages.length) return;
    const newStages = [...stages];
    [newStages[index], newStages[newIndex]] = [newStages[newIndex], newStages[index]];
    newStages.forEach((s, i) => s.ordine = i);
    onChange(newStages);
  };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Tappe dell'itinerario</Label>
        <Button type="button" size="sm" variant="outline" onClick={addStage}>
          <Plus className="w-4 h-4 mr-1" /> Aggiungi tappa
        </Button>
      </div>
      
      {stages.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          Nessuna tappa. Clicca "Aggiungi tappa" per creare un itinerario a tappe.
        </p>
      ) : (
        <div className="space-y-3">
          {stages.map((stage, index) => (
            <div key={index} className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <span className="bg-[#C5A059] text-white px-2 py-0.5 rounded-full text-xs font-medium">
                  Tappa {index + 1}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveStage(index, -1)} className="p-1 hover:bg-gray-200 rounded">
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => moveStage(index, 1)} className="p-1 hover:bg-gray-200 rounded">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => removeStage(index)} className="p-1 hover:bg-red-100 rounded text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Orario</Label>
                  <Input 
                    placeholder="09:00" 
                    value={stage.orario || ''} 
                    onChange={(e) => updateStage(index, 'orario', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Durata</Label>
                  <Input 
                    placeholder="1 ora" 
                    value={stage.durata || ''} 
                    onChange={(e) => updateStage(index, 'durata', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Titolo *</Label>
                  <Input 
                    placeholder="Visita ai Templi" 
                    value={stage.titolo} 
                    onChange={(e) => updateStage(index, 'titolo', e.target.value)}
                    className="h-8 text-sm"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Descrizione</Label>
                  <Textarea 
                    placeholder="Descrizione della tappa..." 
                    value={stage.descrizione || ''} 
                    onChange={(e) => updateStage(index, 'descrizione', e.target.value)}
                    className="text-sm h-16"
                  />
                </div>
                <div>
                  <Label className="text-xs">Località</Label>
                  <Input 
                    placeholder="Paestum" 
                    value={stage.localita || ''} 
                    onChange={(e) => updateStage(index, 'localita', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Link Google Maps</Label>
                  <Input 
                    placeholder="https://maps.google.com/..." 
                    value={stage.link_maps || ''} 
                    onChange={(e) => updateStage(index, 'link_maps', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Attività/Ristorante consigliato</Label>
                  <Input 
                    placeholder="Ristorante Da Mario" 
                    value={stage.attivita_consigliata || ''} 
                    onChange={(e) => updateStage(index, 'attivita_consigliata', e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminItineraries() {
  const { token } = useAuth();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItinerary, setEditingItinerary] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [activeTab, setActiveTab] = useState('base');

  useEffect(() => {
    fetchItineraries();
  }, []);

  const fetchItineraries = async () => {
    try {
      const response = await axios.get(`${API}/admin/itineraries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItineraries(response.data);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      toast.error('Errore caricamento itinerari');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data
    const submitData = {
      ...formData,
      distanza_km: formData.distanza_km ? parseFloat(formData.distanza_km) : null,
      min_partecipanti: formData.min_partecipanti ? parseInt(formData.min_partecipanti) : null,
      max_partecipanti: formData.max_partecipanti ? parseInt(formData.max_partecipanti) : null,
      meteo_config: {
        ...formData.meteo_config,
        temperatura_min: formData.meteo_config.temperatura_min ? parseInt(formData.meteo_config.temperatura_min) : null,
        temperatura_max: formData.meteo_config.temperatura_max ? parseInt(formData.meteo_config.temperatura_max) : null,
        vento_max_kmh: formData.meteo_config.vento_max_kmh ? parseInt(formData.meteo_config.vento_max_kmh) : null,
      },
      tappe: formData.tappe.length > 0 ? formData.tappe : null,
      galleria: formData.galleria.length > 0 ? formData.galleria : null,
    };
    
    try {
      if (editingItinerary) {
        await axios.put(`${API}/admin/itineraries/${editingItinerary.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Itinerario aggiornato!');
      } else {
        await axios.post(`${API}/admin/itineraries`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Itinerario creato!');
      }
      setDialogOpen(false);
      resetForm();
      fetchItineraries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo itinerario?')) return;
    try {
      await axios.delete(`${API}/admin/itineraries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Itinerario eliminato!');
      fetchItineraries();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (itinerary) => {
    setEditingItinerary(itinerary);
    setFormData({
      nome: itinerary.nome || '',
      descrizione: itinerary.descrizione || '',
      condizione_meteo: itinerary.condizione_meteo || 'sunny',
      categoria: itinerary.categoria || 'cultura',
      luogo: itinerary.luogo || '',
      durata: itinerary.durata || '',
      immagine_url: itinerary.immagine_url || '',
      galleria: itinerary.galleria || [],
      link_esterno: itinerary.link_esterno || '',
      ordine: itinerary.ordine || 0,
      attivo: itinerary.attivo !== false,
      distanza_km: itinerary.distanza_km || '',
      tempo_percorrenza: itinerary.tempo_percorrenza || '',
      costo_indicativo: itinerary.costo_indicativo || '',
      cosa_portare: itinerary.cosa_portare || '',
      indicazioni: itinerary.indicazioni || '',
      pubblico: itinerary.pubblico || [],
      difficolta: itinerary.difficolta || '',
      orari_apertura: itinerary.orari_apertura || '',
      tappe: itinerary.tappe || [],
      prenotabile: itinerary.prenotabile || false,
      prenotazione_link: itinerary.prenotazione_link || '',
      prenotazione_whatsapp: itinerary.prenotazione_whatsapp || '',
      prenotazione_note: itinerary.prenotazione_note || '',
      min_partecipanti: itinerary.min_partecipanti || '',
      max_partecipanti: itinerary.max_partecipanti || '',
      meteo_config: itinerary.meteo_config || emptyFormData.meteo_config,
    });
    setActiveTab('base');
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItinerary(null);
    setFormData(emptyFormData);
    setActiveTab('base');
  };

  const getWeatherIcon = (condition) => {
    const found = WEATHER_CONDITIONS.find(w => w.value === condition);
    if (found) {
      const Icon = found.icon;
      return <Icon className="w-5 h-5" />;
    }
    return <Sun className="w-5 h-5" />;
  };

  const togglePubblico = (value) => {
    const current = formData.pubblico || [];
    const newPubblico = current.includes(value)
      ? current.filter(p => p !== value)
      : [...current, value];
    setFormData({ ...formData, pubblico: newPubblico });
  };

  const toggleMeteoIdeale = (value) => {
    const current = formData.meteo_config.meteo_ideale || [];
    const newList = current.includes(value)
      ? current.filter(m => m !== value)
      : [...current, value];
    setFormData({ 
      ...formData, 
      meteo_config: { ...formData.meteo_config, meteo_ideale: newList }
    });
  };

  const toggleMeteoEscluso = (value) => {
    const current = formData.meteo_config.meteo_escluso || [];
    const newList = current.includes(value)
      ? current.filter(m => m !== value)
      : [...current, value];
    setFormData({ 
      ...formData, 
      meteo_config: { ...formData.meteo_config, meteo_escluso: newList }
    });
  };

  // Group itineraries by weather condition
  const groupedItineraries = WEATHER_CONDITIONS.map(condition => ({
    ...condition,
    items: itineraries.filter(i => i.condizione_meteo === condition.value)
  }));

  const tabs = [
    { id: 'base', label: 'Info Base' },
    { id: 'media', label: 'Immagini' },
    { id: 'details', label: 'Dettagli' },
    { id: 'tappe', label: 'Tappe' },
    { id: 'prenotazione', label: 'Prenotazione' },
    { id: 'meteo', label: 'Meteo Avanzato' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-cinzel text-[#1A202C]">Gestione Itinerari</h1>
            <p className="text-[#4A5568] font-manrope text-sm mt-1">
              Gestisci gli itinerari suggeriti in base al meteo ({itineraries.length} totali)
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#C5A059] hover:bg-[#B08D45]"
                onClick={resetForm}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Itinerario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {editingItinerary ? 'Modifica Itinerario' : 'Nuovo Itinerario'}
                </DialogTitle>
              </DialogHeader>
              
              {/* Tabs */}
              <div className="flex gap-1 border-b overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'border-b-2 border-[#C5A059] text-[#C5A059]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                
                {/* Tab: Info Base */}
                {activeTab === 'base' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Nome Itinerario *</Label>
                        <Input
                          value={formData.nome}
                          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                          required
                          placeholder="Es. Spiagge del Cilento"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Descrizione *</Label>
                        <Textarea
                          value={formData.descrizione}
                          onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                          required
                          rows={3}
                          placeholder="Descrizione dell'itinerario..."
                        />
                      </div>
                      
                      <div>
                        <Label>Categoria</Label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={formData.categoria}
                          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                        >
                          {CATEGORIES.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label>Meteo Ideale (base)</Label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={formData.condizione_meteo}
                          onChange={(e) => setFormData({ ...formData, condizione_meteo: e.target.value })}
                        >
                          {WEATHER_CONDITIONS.map(w => (
                            <option key={w.value} value={w.value}>{w.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label>Località *</Label>
                        <Input
                          value={formData.luogo}
                          onChange={(e) => setFormData({ ...formData, luogo: e.target.value })}
                          required
                          placeholder="Es. Paestum"
                        />
                      </div>
                      
                      <div>
                        <Label>Durata</Label>
                        <Input
                          value={formData.durata}
                          onChange={(e) => setFormData({ ...formData, durata: e.target.value })}
                          placeholder="Es. 2-3 ore, mezza giornata"
                        />
                      </div>
                      
                      <div>
                        <Label>Ordine</Label>
                        <Input
                          type="number"
                          value={formData.ordine}
                          onChange={(e) => setFormData({ ...formData, ordine: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 pt-6">
                        <Switch
                          checked={formData.attivo}
                          onCheckedChange={(checked) => setFormData({ ...formData, attivo: checked })}
                        />
                        <Label>Attivo</Label>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Tab: Media */}
                {activeTab === 'media' && (
                  <div className="space-y-6">
                    <ImageUploader
                      value={formData.immagine_url}
                      onChange={(url) => setFormData({ ...formData, immagine_url: url })}
                      label="Immagine di copertina"
                      token={token}
                    />
                    
                    <GalleryUploader
                      images={formData.galleria || []}
                      onChange={(images) => setFormData({ ...formData, galleria: images })}
                      token={token}
                    />
                  </div>
                )}
                
                {/* Tab: Dettagli */}
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Distanza dalla Maisonette (km)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={formData.distanza_km}
                          onChange={(e) => setFormData({ ...formData, distanza_km: e.target.value })}
                          placeholder="Es. 5.5"
                        />
                      </div>
                      
                      <div>
                        <Label>Tempo di percorrenza</Label>
                        <Input
                          value={formData.tempo_percorrenza}
                          onChange={(e) => setFormData({ ...formData, tempo_percorrenza: e.target.value })}
                          placeholder="Es. 20 min in auto"
                        />
                      </div>
                      
                      <div>
                        <Label>Costo indicativo</Label>
                        <Input
                          value={formData.costo_indicativo}
                          onChange={(e) => setFormData({ ...formData, costo_indicativo: e.target.value })}
                          placeholder="Es. €€, Gratuito, €15-25"
                        />
                      </div>
                      
                      <div>
                        <Label>Difficoltà</Label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={formData.difficolta}
                          onChange={(e) => setFormData({ ...formData, difficolta: e.target.value })}
                        >
                          <option value="">-- Seleziona --</option>
                          {DIFFICOLTA_OPTIONS.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label>Orari apertura</Label>
                        <Input
                          value={formData.orari_apertura}
                          onChange={(e) => setFormData({ ...formData, orari_apertura: e.target.value })}
                          placeholder="Es. 09:00-19:00, sempre aperto"
                        />
                      </div>
                      
                      <div>
                        <Label>Link esterno</Label>
                        <Input
                          value={formData.link_esterno}
                          onChange={(e) => setFormData({ ...formData, link_esterno: e.target.value })}
                          placeholder="https://maps.google.com/..."
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Pubblico consigliato</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {PUBBLICO_OPTIONS.map(p => (
                            <button
                              key={p.value}
                              type="button"
                              onClick={() => togglePubblico(p.value)}
                              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                                (formData.pubblico || []).includes(p.value)
                                  ? 'bg-[#C5A059] text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Cosa portare</Label>
                        <Textarea
                          value={formData.cosa_portare}
                          onChange={(e) => setFormData({ ...formData, cosa_portare: e.target.value })}
                          placeholder="Es. Crema solare, scarpe comode, cappello..."
                          rows={2}
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Indicazioni utili</Label>
                        <Textarea
                          value={formData.indicazioni}
                          onChange={(e) => setFormData({ ...formData, indicazioni: e.target.value })}
                          placeholder="Consigli pratici per l'itinerario..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Tab: Tappe */}
                {activeTab === 'tappe' && (
                  <StagesEditor
                    stages={formData.tappe || []}
                    onChange={(tappe) => setFormData({ ...formData, tappe })}
                  />
                )}
                
                {/* Tab: Prenotazione */}
                {activeTab === 'prenotazione' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                      <Switch
                        checked={formData.prenotabile}
                        onCheckedChange={(checked) => setFormData({ ...formData, prenotabile: checked })}
                      />
                      <div>
                        <Label className="text-base font-medium">Esperienza prenotabile</Label>
                        <p className="text-xs text-gray-500">
                          Attiva per mostrare il pulsante "Richiedi prenotazione" sul sito
                        </p>
                      </div>
                    </div>
                    
                    {formData.prenotabile && (
                      <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
                        <div className="col-span-2">
                          <Label>Link prenotazione esterna</Label>
                          <Input
                            value={formData.prenotazione_link}
                            onChange={(e) => setFormData({ ...formData, prenotazione_link: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label>Messaggio WhatsApp precompilato</Label>
                          <Input
                            value={formData.prenotazione_whatsapp}
                            onChange={(e) => setFormData({ ...formData, prenotazione_whatsapp: e.target.value })}
                            placeholder="Vorrei prenotare l'esperienza..."
                          />
                        </div>
                        
                        <div>
                          <Label>Min partecipanti</Label>
                          <Input
                            type="number"
                            value={formData.min_partecipanti}
                            onChange={(e) => setFormData({ ...formData, min_partecipanti: e.target.value })}
                            placeholder="1"
                          />
                        </div>
                        
                        <div>
                          <Label>Max partecipanti</Label>
                          <Input
                            type="number"
                            value={formData.max_partecipanti}
                            onChange={(e) => setFormData({ ...formData, max_partecipanti: e.target.value })}
                            placeholder="10"
                          />
                        </div>
                        
                        <div className="col-span-2">
                          <Label>Note prenotazione</Label>
                          <Textarea
                            value={formData.prenotazione_note}
                            onChange={(e) => setFormData({ ...formData, prenotazione_note: e.target.value })}
                            placeholder="Info aggiuntive sulla prenotazione..."
                            rows={2}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Tab: Meteo Avanzato */}
                {activeTab === 'meteo' && (
                  <div className="space-y-4">
                    <div>
                      <Label>Condizioni meteo ideali</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {WEATHER_CONDITIONS.map(w => {
                          const Icon = w.icon;
                          return (
                            <button
                              key={w.value}
                              type="button"
                              onClick={() => toggleMeteoIdeale(w.value)}
                              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                                (formData.meteo_config.meteo_ideale || []).includes(w.value)
                                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Condizioni meteo escluse</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {[...WEATHER_CONDITIONS, { value: 'stormy', label: 'Temporale', icon: AlertTriangle }].map(w => {
                          const Icon = w.icon;
                          return (
                            <button
                              key={w.value}
                              type="button"
                              onClick={() => toggleMeteoEscluso(w.value)}
                              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                                (formData.meteo_config.meteo_escluso || []).includes(w.value)
                                  ? 'bg-red-100 text-red-700 border-2 border-red-300'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="flex items-center gap-1">
                          <Thermometer className="w-4 h-4" /> Temp. minima (°C)
                        </Label>
                        <Input
                          type="number"
                          value={formData.meteo_config.temperatura_min || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, temperatura_min: e.target.value }
                          })}
                          placeholder="10"
                        />
                      </div>
                      
                      <div>
                        <Label className="flex items-center gap-1">
                          <Thermometer className="w-4 h-4" /> Temp. massima (°C)
                        </Label>
                        <Input
                          type="number"
                          value={formData.meteo_config.temperatura_max || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, temperatura_max: e.target.value }
                          })}
                          placeholder="35"
                        />
                      </div>
                      
                      <div>
                        <Label className="flex items-center gap-1">
                          <Wind className="w-4 h-4" /> Vento max (km/h)
                        </Label>
                        <Input
                          type="number"
                          value={formData.meteo_config.vento_max_kmh || ''}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, vento_max_kmh: e.target.value }
                          })}
                          placeholder="30"
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.meteo_config.attivita_indoor}
                          onCheckedChange={(checked) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, attivita_indoor: checked }
                          })}
                        />
                        <Label>Attività al chiuso</Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.meteo_config.attivita_outdoor}
                          onCheckedChange={(checked) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, attivita_outdoor: checked }
                          })}
                        />
                        <Label>Attività all'aperto</Label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.meteo_config.attivita_marina}
                          onCheckedChange={(checked) => setFormData({ 
                            ...formData, 
                            meteo_config: { ...formData.meteo_config, attivita_marina: checked }
                          })}
                        />
                        <Label>Attività marina</Label>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Submit */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-[#C5A059] hover:bg-[#B08D45]">
                    {editingItinerary ? 'Salva Modifiche' : 'Crea Itinerario'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]" />
          </div>
        ) : (
          <div className="space-y-6">
            {groupedItineraries.map(group => (
              <Card key={group.value}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg font-cinzel">
                    <group.icon className="w-5 h-5 text-[#C5A059]" />
                    {group.label}
                    <span className="text-sm font-normal text-gray-500">
                      ({group.items.length} itinerar{group.items.length === 1 ? 'io' : 'i'})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {group.items.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4 text-center">
                      Nessun itinerario per questa condizione meteo
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {group.items.map(it => (
                        <div 
                          key={it.id}
                          className={`flex items-center gap-4 p-3 rounded-lg border ${
                            it.attivo ? 'bg-white' : 'bg-gray-100 opacity-60'
                          }`}
                        >
                          {/* Image thumbnail */}
                          {it.immagine_url ? (
                            <img 
                              src={it.immagine_url} 
                              alt={it.nome}
                              className="w-16 h-16 object-cover rounded-lg"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Image className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium truncate">{it.nome}</h4>
                              {it.prenotabile && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                  Prenotabile
                                </span>
                              )}
                              {!it.attivo && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                                  Bozza
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {it.luogo}
                              </span>
                              {it.durata && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {it.durata}
                                </span>
                              )}
                              <span className="px-2 py-0.5 bg-gray-100 rounded">
                                {CATEGORIES.find(c => c.value === it.categoria)?.label || it.categoria}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(it)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(it.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
